import React from 'react'
import { cn } from './cn'

export interface PlanFeatureGroup {
  heading: string
  lines: string[]
}

export interface PlanCardProps {
  name: React.ReactNode
  /** .featured：最熱門方案外框 */
  featured?: boolean
  /** .selected：選中/處理中態 */
  selected?: boolean
  /** 主價格（已 format 的字串，如 "NT$330"；Latin 數字走 Playfair via .plan-price.num） */
  price: React.ReactNode
  /** 週期後綴節點，如 "/月"（含斜線）；免費方案省略 → 不渲染 .per */
  per?: React.ReactNode
  sub: React.ReactNode
  featureGroups: PlanFeatureGroup[]
  /** featured 時的「最熱門」標籤（呼叫端傳 <Tag fill>…）；其餘省略 */
  badge?: React.ReactNode
  /** CTA 按鈕節點（呼叫端自帶 variant / disabled / onClick 邏輯） */
  cta: React.ReactNode
  /** 卡片本體點擊（視覺選取；CTA 自行 stopPropagation） */
  onClick?: React.MouseEventHandler<HTMLDivElement>
}

/**
 * 方案卡（.plan-card）：封裝 pricing 頁手寫的卡片 markup（.plan-name + .plan-price.num
 * + .plan-sub + .plan-feat 分組清單 + CTA）。同 class、零視覺差。
 */
export function PlanCard({
  name,
  featured,
  selected,
  price,
  per,
  sub,
  featureGroups,
  badge,
  cta,
  onClick,
}: PlanCardProps) {
  const fullWidthCta = React.isValidElement<{ className?: string }>(cta)
    ? React.cloneElement(cta, {
        className: cn(cta.props.className, 'flex w-full justify-center'),
      })
    : cta

  return (
    <div
      data-plan-card
      data-featured={featured || undefined}
      data-selected={selected || undefined}
      className={cn(
        'flex flex-col gap-[var(--gap-sm)] rounded-[var(--radius-lg)] border border-border bg-background p-[var(--inset-card)] transition-[border-color] duration-150',
        selected && '[border-width:var(--border-emphasis)] border-foreground',
        onClick && 'cursor-pointer hover:border-foreground'
      )}
      onClick={onClick}
    >
      <div data-plan-name className="flex items-center gap-[var(--gap-xs)] text-md font-semibold">
        {name}
        {badge}
      </div>
      <div data-plan-price className="font-display text-[length:var(--fs-price)] font-semibold leading-[var(--leading-display)] tabular-nums">
        {price}
        {per != null ? <span className="font-body text-md font-normal text-muted">{per}</span> : null}
      </div>
      <p className="min-h-[var(--gap-md)] text-sm leading-[var(--leading-body)] text-muted">{sub}</p>
      <ul className="mt-[var(--gap-xs)] flex flex-1 list-none flex-col gap-[var(--gap-xs)] p-0 text-base leading-[var(--leading-body)]">
        {featureGroups.map((group) => (
          <React.Fragment key={group.heading}>
            <li
              data-plan-feature-group
              className="mt-[var(--gap-2xs)] pl-0 font-mono text-3xs uppercase tracking-[var(--tracking-label)] text-muted first:mt-0"
            >
              {group.heading}
            </li>
            {group.lines.map((line) => (
              <li
                key={line}
                className='relative pl-[var(--gap-md)] before:absolute before:left-0 before:font-mono before:text-xs before:content-["✓"]'
              >
                {line}
              </li>
            ))}
          </React.Fragment>
        ))}
      </ul>
      {fullWidthCta}
    </div>
  )
}
